import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Box, Button, Container, CssBaseline, Stack, Toolbar, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ChatWidget from './components/ChatWidget';
import LoginPage from './components/LoginPage';
import AdminDashboard from './components/AdminDashboard';
import { loadToken, setAuthToken, setAuthFailureHandler } from './api';

function AppContent({ auth, onLogin, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSessionExpired = useCallback(() => {
    onLogout();
    navigate('/admin/dashboard/overview', { replace: true });
  }, [navigate, onLogout]);

  useEffect(() => {
    setAuthFailureHandler(handleSessionExpired);
    return () => setAuthFailureHandler(() => {});
  }, [handleSessionExpired]);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isEmbedRoute = location.pathname === "/embed";

  if (isEmbedRoute) {
    return <ChatWidget mode="public" />;
  }

  return (
    <>
      {isAdminRoute && auth && (
        <AppBar position="sticky" elevation={0}>
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexGrow: 1 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "9px",
                  background: "linear-gradient(135deg,#6366f1,#4338ca)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 15,
                  boxShadow: "0 2px 6px rgba(79,70,229,0.4)",
                }}
              >
                C
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                Chatbot Control
              </Typography>
            </Box>
            {auth && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button component={Link} to="/admin/dashboard/overview" color="inherit" sx={{ fontWeight: 600 }}>
                  Dashboard
                </Button>
                <Button onClick={onLogout} variant="outlined" color="inherit" sx={{ borderColor: "divider" }}>
                  Logout
                </Button>
              </Stack>
            )}
          </Toolbar>
        </AppBar>
      )}
      <Container maxWidth={isAdminRoute ? false : "xl"} disableGutters>
        <Box sx={{ minHeight: isAdminRoute && auth ? "calc(100vh - 64px)" : "100vh" }}>
          <Routes>
            <Route path="/" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route path="/admin/chat" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route path="/admin/dashboard/*" element={auth ? <AdminDashboard role={auth.role} userEmail={auth.email} onLogout={onLogout} tenantId={auth.tenant_id} tenantIds={auth.tenant_ids || []} /> : <LoginPage onLogin={onLogin} />} />
            <Route path="/admin/dashboard" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </Box>
      </Container>
    </>
  );
}

function App() {
  const [auth, setAuth] = useState(null);
  const theme = createTheme({
    palette: {
      mode: "light",
      primary: { main: "#4f46e5", light: "#6366f1", dark: "#4338ca", contrastText: "#ffffff" },
      secondary: { main: "#0ea5e9" },
      success: { main: "#16a34a" },
      error: { main: "#dc2626" },
      warning: { main: "#d97706" },
      info: { main: "#2563eb" },
      background: { default: "#f4f6fb", paper: "#ffffff" },
      text: { primary: "#1e293b", secondary: "#64748b" },
      divider: "#e6e9f0",
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      h4: { fontWeight: 800, letterSpacing: "-0.02em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 700, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: "1px solid #e6e9f0",
            borderRadius: 16,
            boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 2px 6px rgba(16,24,40,0.05)",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 18, paddingBlock: 7 },
          containedPrimary: {
            color: "#ffffff",
            background: "linear-gradient(180deg,#5b53ea 0%,#4f46e5 100%)",
            "&:hover": { background: "linear-gradient(180deg,#4f46e5 0%,#4338ca 100%)" },
            "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(255,255,255,0.85)",
            backdropFilter: "saturate(180%) blur(8px)",
            color: "#1e293b",
            borderBottom: "1px solid #e6e9f0",
            boxShadow: "none",
          },
        },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 16 } } },
      MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
      MuiTableCell: { styleOverrides: { head: { fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc" } } },
    },
  });

  useEffect(() => {
    if (loadToken()) {
      const cachedRole = localStorage.getItem("role");
      const cachedTenant = localStorage.getItem("tenant_id");
      const cachedTenantIdsRaw = localStorage.getItem("tenant_ids");
      let cachedTenantIds = [];
      if (cachedTenantIdsRaw) {
        try {
          cachedTenantIds = JSON.parse(cachedTenantIdsRaw);
        } catch (err) {
          cachedTenantIds = [];
        }
      }
      if (cachedRole) setAuth({ role: cachedRole, email: localStorage.getItem("email") || "", tenant_id: cachedTenant, tenant_ids: cachedTenantIds });
    }
  }, []);

  const onLogin = (data) => {
    setAuth(data);
    localStorage.setItem("role", data.role);
    localStorage.setItem("email", data.email || "");
    localStorage.setItem("tenant_id", data.tenant_id || "");
    localStorage.setItem("tenant_ids", JSON.stringify(data.tenant_ids || []));
  };

  const onLogout = () => {
    setAuthToken(null);
    setAuth(null);
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    localStorage.removeItem("tenant_id");
    localStorage.removeItem("tenant_ids");
    localStorage.removeItem("admin_dashboard_selected_tenant_id");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent auth={auth} onLogin={onLogin} onLogout={onLogout} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App; 